/**
 * React Query hooks for API token management.
 *
 * Tokens are used for programmatic access (e.g. Claude Code MCP integration).
 * The full token value is only available in the `createApiToken` response and
 * is never surfaced again — only the prefix is stored.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listApiTokens, createApiToken, deleteApiToken } from '../api/client';
import type { CreateApiTokenRequest } from '../types';

const API_TOKENS_KEY = ['api-tokens'] as const;

export function useApiTokens() {
  return useQuery({
    queryKey: API_TOKENS_KEY,
    queryFn: listApiTokens,
  });
}

export function useCreateApiToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateApiTokenRequest) => createApiToken(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: API_TOKENS_KEY });
    },
  });
}

export function useDeleteApiToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteApiToken(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: API_TOKENS_KEY });
    },
  });
}
